import { motion } from "framer-motion";
import { FileText, HardDrive } from "lucide-react";
import type { SearchHit } from "../lib/api";
import Highlight from "./Highlight";

function formatBytes(n: number): string {
  if (!isFinite(n) || n <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  return `${(n / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatDate(t: number): string {
  if (!t) return "—";
  const ms = t < 1e12 ? t * 1000 : t;
  return new Date(ms).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });
}

interface ResultCardProps {
  hit: SearchHit;
  query: string;
  onSelect: () => void;
}

export default function ResultCard({ hit, query, onSelect }: ResultCardProps) {
  const score = Math.max(0, Math.min(1, hit.score ?? 0));
  return (
    <motion.button
      type="button"
      onClick={onSelect}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
      whileHover={{ y: -1 }}
      className="glass glass-hover ring-glass p-5 text-left w-full block group"
    >
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 shrink-0 rounded-lg bg-white/[0.05] border border-white/[0.10] flex items-center justify-center text-accent-blue">
          <FileText size={18} strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-3">
            <h3 className="text-base md:text-lg font-semibold text-slate-100 truncate">
              <Highlight text={hit.filename} query={query} />
            </h3>
            <span className="font-mono text-[11px] text-slate-500 shrink-0">
              {formatDate(hit.mtime)}
            </span>
          </div>
          <div className="mt-1 flex items-center gap-3 text-[11px] font-mono text-slate-500">
            <span className="inline-flex items-center gap-1">
              <HardDrive size={11} strokeWidth={2} />
              {hit.machine || "unknown"}
            </span>
            <span>{formatBytes(hit.size || 0)}</span>
            {hit.path && (
              <span className="truncate text-slate-600" title={hit.path}>
                {hit.path}
              </span>
            )}
          </div>
          {hit.snippet && (
            <p className="mt-3 text-sm leading-relaxed text-slate-300 line-clamp-3">
              <Highlight text={hit.snippet} query={query} />
            </p>
          )}
          <div className="mt-4 flex items-center gap-2">
            <div className="h-[3px] flex-1 bg-white/[0.05] rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-accent-blue via-accent-cyan to-accent-violet"
                style={{ width: `${(score * 100).toFixed(0)}%` }}
              />
            </div>
            <span className="text-[10px] font-mono text-slate-500 tabular-nums">
              {(score * 100).toFixed(0)}%
            </span>
          </div>
        </div>
      </div>
    </motion.button>
  );
}
